import type {
	FeedMediaItem,
	ReelItem
} from '~/components/PropertyReelsFeed/types';
import type { Property } from '~/core/entities/property';

import { useFeedState, useVideoHistory } from '~/composables/shorts';
import {
	mapPropertyToFeedItem,
	toShortVideoProperty
} from '~/composables/shorts/mappers';
import { uniqueFeedItemsByCommercialId } from '~/composables/shorts/useFeedSession';
import { shortVideoToReelItem } from '~/composables/shorts/useVideoHistory';
import { useShortsFeedStore } from '~/store/shortsFeedStore';
import { useVideoHistoryStore } from '~/store/videoHistoryStore';
import { storeToRefs } from 'pinia';
import { toRaw } from 'vue';

export const END_OF_RECOMMENDATION_CAROUSEL_COUNT = 3;

interface SetTargetOptions {
	countsTowardEndOfRecommendation?: boolean;
}

function withNativePlayback(item: FeedMediaItem): ReelItem {
	return {
		...item,
		videoType: 'native',
		property: toRaw(item.property)
	};
}

function indexAfterLastVideo(items: ReelItem[]): number {
	for (let index = items.length - 1; index >= 0; index -= 1) {
		if (items[index]?.kind === 'video') return index + 1;
	}

	return 0;
}

export function useShortsFeedOrchestrator() {
	const feedStore = useShortsFeedStore();
	const historyStore = useVideoHistoryStore();

	const { loadHistory, loadPrevious, resetHistory } = useVideoHistory();
	const {
		loadMore: loadMoreFeed,
		reset: resetFeed,
		seedFromCarousel
	} = useFeedState();

	const {
		videos,
		hasMore: hasMorePages,
		allWatched,
		isLoading: isFeedLoading,
		recommendationCountVideos,
		recommendationCountPhotos,
		isSeededFromCarousel
	} = storeToRefs(feedStore);
	const { historyItems, hasMoreHistory, isLoadingHistory } =
		storeToRefs(historyStore);

	const targetItem = ref<ReelItem | null>(null);
	const targetCountsTowardEndOfRecommendation = ref(false);
	const targetCommercialId = computed(
		() => targetItem.value?.property.commercialId
	);
	const sessionItems = computed(() => videos.value.map(shortVideoToReelItem));
	const feedWithoutTarget = computed(() =>
		sessionItems.value.filter(
			item => item.property.commercialId !== targetCommercialId.value
		)
	);
	const recommendationItems = computed(() => {
		if (!targetCountsTowardEndOfRecommendation.value || !targetItem.value) {
			return feedWithoutTarget.value;
		}

		return [targetItem.value, ...feedWithoutTarget.value];
	});
	const seededFeedCommercialIds = computed(() =>
		isSeededFromCarousel.value
			? new Set(videos.value.map(item => item.property.commercialId))
			: new Set<string>()
	);
	/**
	 * Os carrosséis finais são artefato do fim de recomendação, não histórico:
	 * mesmo já vistos, precisam continuar depois do último Short em vez de
	 * subirem para o topo do feed.
	 */
	const endOfRecommendationTailCommercialIds = computed(() => {
		const items = uniqueFeedItemsByCommercialId(recommendationItems.value);
		const startIndex = indexAfterLastVideo(items);

		return new Set(
			items
				.slice(startIndex, startIndex + END_OF_RECOMMENDATION_CAROUSEL_COUNT)
				.map(item => item.property.commercialId)
		);
	});
	const visibleHistoryItems = computed(() =>
		uniqueFeedItemsByCommercialId(
			historyItems.value.filter(
				item =>
					item.property.commercialId !== targetCommercialId.value &&
					!seededFeedCommercialIds.value.has(item.property.commercialId) &&
					!endOfRecommendationTailCommercialIds.value.has(
						item.property.commercialId
					)
			)
		).map(item => ({
			...item,
			reelKey: `h:${item.property.commercialId}`
		}))
	);
	const visibleRecommendationItems = computed(() => {
		const historyCommercialIds = new Set(
			visibleHistoryItems.value.map(item => item.property.commercialId)
		);

		return uniqueFeedItemsByCommercialId(
			recommendationItems.value.filter(
				item => !historyCommercialIds.has(item.property.commercialId)
			)
		);
	});
	const endOfRecommendationCarouselStartIndex = computed(() =>
		indexAfterLastVideo(visibleRecommendationItems.value)
	);
	const endOfRecommendationCarousels = computed(() =>
		visibleRecommendationItems.value.slice(
			endOfRecommendationCarouselStartIndex.value,
			endOfRecommendationCarouselStartIndex.value +
				END_OF_RECOMMENDATION_CAROUSEL_COUNT
		)
	);
	const hasReachedEndOfRecommendation = computed(() => {
		if (isFeedLoading.value) return false;
		if (visibleRecommendationItems.value.length === 0) return false;

		const carousels = endOfRecommendationCarousels.value;
		if (!carousels.every(item => item.kind === 'fotos')) return false;

		/**
		 * Paginação esgotada já é "não há mais nenhum Short disponível"
		 * (PP-2261). Os três carrosséis são o alvo da cauda, não uma
		 * pré-condição: exigi-los aqui, sem nada a carregar, é o que trava o
		 * feed sem pedir mais e sem tela de fim ao mesmo tempo.
		 */
		if (!hasMorePages.value) return true;

		return (
			allWatched.value &&
			carousels.length === END_OF_RECOMMENDATION_CAROUSEL_COUNT
		);
	});
	const visibleFeedItems = computed(() => {
		const visibleRecommendations = hasReachedEndOfRecommendation.value
			? visibleRecommendationItems.value.slice(
					0,
					endOfRecommendationCarouselStartIndex.value +
						END_OF_RECOMMENDATION_CAROUSEL_COUNT
				)
			: visibleRecommendationItems.value;

		return targetCountsTowardEndOfRecommendation.value && targetItem.value
			? visibleRecommendations.slice(1)
			: visibleRecommendations;
	});
	const hasMore = computed(
		() => hasMorePages.value && !hasReachedEndOfRecommendation.value
	);

	/** A única composição: histórico, alvo e lista da sessão, nessa ordem. */
	const reelItems = computed<ReelItem[]>(() => {
		const history = visibleHistoryItems.value;
		const target = targetItem.value
			? [
					{
						...targetItem.value,
						reelKey: `t:${targetItem.value.property.commercialId}`
					}
				]
			: [];
		const feed = visibleFeedItems.value.map(item => ({
			...item,
			reelKey: `f:${item.property.commercialId}`
		}));

		return uniqueFeedItemsByCommercialId([...history, ...target, ...feed]);
	});
	const historyCount = computed(() => {
		const firstNonHistory = reelItems.value.findIndex(
			item => !item.reelKey?.startsWith('h:')
		);
		return firstNonHistory === -1 ? reelItems.value.length : firstNonHistory;
	});

	const isLoading = computed(
		() => isFeedLoading.value || isLoadingHistory.value
	);

	function setTargetFromShortVideo(
		item: FeedMediaItem,
		options: SetTargetOptions = {}
	): void {
		targetItem.value = withNativePlayback(item);
		targetCountsTowardEndOfRecommendation.value =
			options.countsTowardEndOfRecommendation ?? false;
	}

	function setTargetFromProperty(
		property: Property,
		options: SetTargetOptions = {}
	): void {
		const mappedItem = mapPropertyToFeedItem(toShortVideoProperty(property));
		if (!mappedItem) {
			throw createError({
				statusCode: 404,
				statusMessage: 'Imóvel sem short disponível'
			});
		}

		setTargetFromShortVideo(mappedItem, options);
	}

	function resetAll(): void {
		targetItem.value = null;
		targetCountsTowardEndOfRecommendation.value = false;
		resetHistory();
		resetFeed();
	}

	function resetHistoryAndFeed(): void {
		resetHistory();
		resetFeed();
	}

	return {
		reelItems,
		historyCount,
		hasMore,
		hasReachedEndOfRecommendation,
		hasMoreHistory,
		isLoading,
		recommendationCountVideos,
		recommendationCountPhotos,
		loadHistory,
		loadPrevious,
		loadMoreHistory: loadPrevious,
		loadMoreFeed,
		setTargetFromProperty,
		setTargetFromShortVideo,
		resetAll,
		resetHistoryAndFeed,
		seedFromCarousel
	};
}
